<template>
  <div v-if="!loading">
    <div class="event-wizard">
      <CContainer>
        <h5 class="" v-translate translate-context="event_gallery">Settings</h5>
        <p class="text-help-dark">
          <translate translate-context="event_gallery.subtitle">Edit the gallery settings</translate>
        </p>
        <CRow>
          <CCol col="12">
            <PFRow size="7x5" :label="$pgettext('event_gallery.label', 'Name')" label-class="big" separator
                   v-if="hasPerm('gallery.settings.name')">
              <PTranslatableTextInput v-model="form.name"/>
            </PFRow>
            <PFRow size="7x5" :label="$pgettext('event_gallery.label', 'Free')"
                   :help-text="$pgettext('event_gallery.help', 'Make the media uploaded to this gallery available for free')"
                   separator
                   label-class="big"
                   v-if="hasPerm('gallery.settings.is_free')"
            >
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.is_free"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Public')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.is_public')"
                   :help-text="$pgettext('event_gallery.help', 'Public gallery visible to all event participants')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.is_public"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Show in \'All Photos\'')"
                   label-class="big"
                   separator
                   :help-text="$pgettext('event_gallery.help', 'Make the photos in this gallery visible to all participants')"
                    v-if="hasPerm('gallery.settings.show_in_all_photos') && !form.is_public && form.is_free">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.show_in_all_photos"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Show Media in Parent Gallery')"
                   label-class="big"
                   separator
                   :help-text="$pgettext('event_gallery.help', 'Show media from sub-galleries in the parent gallery view')"
                   v-if="hasPerm('gallery.settings.show_media_in_parent_gallery')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.show_media_in_parent_gallery"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Hide Gallery Name')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.hide_name')"
                   :help-text="$pgettext('event_gallery.help', 'Do not display in preview')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.hide_name"/>
            </PFRow>
            <PFRow size="7x5" :label="$pgettext('event_gallery.label', 'Media sorting')" label-class="big" separator
                   v-if="hasPerm('gallery.settings.sorting')">
              <PSelectKeyValue
                  :options="sortingOptions"
                  :value.sync="form.sorting"
                  :placeholder="$pgettext('event_gallery.placeholder','Select media sort type')"
              />
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Expanded Display Type')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.expanded_display_type')"
                   :help-text="$pgettext('event_gallery.help', 'Choose how to arrange your elements in the expanded view')">
              <CSelect
                  :options="expandedDisplayTypes"
                  v-model="form.expanded_display_type"
                  :placeholder="$pgettext('event_gallery.placeholder','Select expanded display type')"
              />
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Compact Display Type')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.compact_display_type')"
                   :help-text="$pgettext('event_gallery.help', 'Choose how to arrange your elements in the compact view')">
              <CSelect
                  :options="compactDisplayTypes"
                  v-model="form.compact_display_type"
                  :placeholder="$pgettext('event_gallery.placeholder','Select compact display type')"
              />
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Sub Gallery Display')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.sub_gallery_display') && form.active"
                   :help-text="$pgettext('event_gallery.help', 'Choose how to display sub galleries')">
              <CSelect
                  :options="subGalleryDisplayTypes"
                  v-model="form.sub_gallery_display"
                  :placeholder="$pgettext('event_gallery.placeholder','Select sub gallery display type')"
              />
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Shareable')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.is_shareable')"
                   :help-text="$pgettext('event_gallery.help', 'Allow users to share the gallery')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.is_shareable"/>
            </PFRow>
            <PFRow size="7x5" v-if="tags && hasPerm('gallery.settings.tags')"
                   :label="$pgettext('event_gallery.label', 'Tags')"
                   label-class="big"
                   separator
                   :help-text="$pgettext('event_gallery.help', 'Set specific tags for this gallery')">
              <PTreeTagInput v-model="form.tags" :allowed_tags="tags" :multiple="true"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Processing pipeline')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.pipeline')"
                   :help-text="$pgettext('event_gallery.help', 'Enforce a processing pipeline for uploads to this gallery')">
              <template #label-append>
                <ProcessingPipelineHelp/>
              </template>
              <CSelect
                  :options="processingPipelineOptions"
                  v-model="form.pipeline"
              />
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Image enhancement')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.enhance_img')"
                   :help-text="$pgettext('event_gallery.help', 'Improve the image quality')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.enhance_img"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Passive')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.passive')"
                   :help-text="$pgettext('event_gallery.help', 'Disable browse photo/upload for this gallery, media will be received using tags/releases')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.passive"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Activate')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.active')"
                   :help-text="$pgettext('event_gallery.help', 'Turn the gallery on or off')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.active"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Order')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.order')"
                   :help-text="$pgettext('event_gallery.help', 'Order the gallery (ascending 0->100)')">
              <CInput v-model="form.order" type="number"/>
            </PFRow>
            <PFRow size="7x5" :label="$pgettext('event_gallery.label', 'Description (Dashboard only)')"
                   label-class="big"
                   separator v-if="hasPerm('gallery.settings.description')">
              <PTranslatableCkeditor v-model="form.description"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Max Uploads')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.max_media')"
                   :help-text="$pgettext('event_gallery.help', 'Max media for this gallery (0 = Unlimited)')">
              <CInput v-model="form.max_media" type="number"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Favourite Eligibility')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.favorites_eligibility')"
                   :help-text="$pgettext('event_gallery.help', 'Set favourite eligibility for this gallery')">
              <CSelect v-model="form.favorites_eligibility" :options="favouriteEligibility" :placeholder="$pgettext('event_gallery.placeholder','Select favourite eligibility')"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Default Gallery')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.is_default')"
                   :help-text="$pgettext('event_gallery.help', 'Set this gallery as the default for mobile app uploads')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="form.is_default"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Auto Refresh')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.refresh_time')"
                   :help-text="$pgettext('event_gallery.help', 'Enable automatic refresh notifications for new media')">
              <CSwitch class="mx-1" color="primary" variant="3d" size="sm"
                       :checked.sync="autoRefreshEnabled"/>
            </PFRow>
            <PFRow size="7x5"
                   :label="$pgettext('event_gallery.label', 'Refresh Time (seconds)')"
                   label-class="big"
                   separator
                   v-if="hasPerm('gallery.settings.refresh_time') && autoRefreshEnabled"
                   :help-text="$pgettext('event_gallery.help', 'Time interval in seconds for checking new media. Users will see a popup after this time proposing to refresh the page when new media is available. Range: 1-300 seconds.')">
              <CInput v-model="form.refresh_time" type="number" min="1" max="300"/>
            </PFRow>
            <template v-if="isDeletable">
              <PFRow size="7x5" :help-text="$pgettext('event_gallery.help', 'This will delete the gallery permanently')"
                     separator>
                <template #title>
                  <label class="big text-danger"> {{ $pgettext('event_gallery.label', 'Delete Gallery') }}</label>
                </template>
                <CButton @click="onDeleteGallery"
                         class="btn-circle"
                         color="dark"
                         variant="outline">
                  <CIcon name="cipTrashFull"/>
                </CButton>
              </PFRow>
            </template>
          </CCol>
        </CRow>
      </CContainer>
      <TabFooter fluid @save="onSave" @reset="onReset"/>
    </div>
  </div>
</template>

<script>
import PFRow from "@/domain/core/components/PFRow.vue";
import PTranslatableTextInput from "@/domain/core/components/PTranslatableTextInput.vue";
import PTreeTagInput from "@/domain/core/components/PTreeTagInput.vue";
import TabFooter from "@/domain/eventGallery/components/TabFooter.vue";
import galleryLayoutTypes from "@/domain/core/constant/galleryLayoutTypes";
import PSelectKeyValue from "@/domain/core/components/PSelectKeyValue.vue";
import gallerySorting from "@/domain/core/constant/gallerySorting";
import {mapActions, mapGetters} from "vuex";
import PTranslatableCkeditor from "@/domain/core/components/PTranslatableCkeditor.vue";
import ProcessingPipelineHelp from "@/domain/eventGallery/components/ProcessingPipelineHelp.vue";

export default {
  name: "TabSetting",
  components: {
    ProcessingPipelineHelp, PTranslatableCkeditor, PSelectKeyValue, TabFooter, PTreeTagInput,
    PTranslatableTextInput, PFRow
  },
  props: {
    isDeletable: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapGetters('eventGallery', [
      'tags',
      'gallery',
      'step_setting'
    ]),
    ...mapGetters('core', [
      'favouriteEligibility',
      'subGalleryDisplayTypes',
      'compactDisplayTypes',
      'expandedDisplayTypes',
      'processingPipelines',
    ]),
    processingPipelineOptions() {
      return [
        {
          label: this.$pgettext('event_gallery.processing_pipeline', 'Default'),
          value: null,
        },
        ...this.processingPipelines,
      ]
    },
    sortingOptions() {
      return gallerySorting.options()
    },
    autoRefreshEnabled: {
      get() {
        return this.form.refresh_time > 0
      },
      set(value) {
        this.form.refresh_time = value ? 15 : 0
      }
    }
  },
  data() {
    return {
      form: {
        name: {},
        description: {},
        is_free: false,
        is_public: false,
        show_in_all_photos: true,
        show_media_in_parent_gallery: false,
        is_shareable: true,
        enhance_img: false,
        hide_name: false,
        passive: false,
        sorting: gallerySorting.default(),
        layout: galleryLayoutTypes.default(),
        expanded_display_type: null,
        compact_display_type: null,
        sub_gallery_display: null,
        pipeline: null,
        order: 0,
        active: true,
        max_media: 0,
        favorites_eligibility: null,
        is_default: false,
        refresh_time: 15,
      },
      loading: true,
    }
  },
  emits: ['save', 'delete'],
  async mounted() {
    await Promise.all([
      this.fetchTabData('setting'),
      this.fetchTags(),
      this.fetchSubGalleryDisplayTypes(),
      this.fetchCompactDisplayTypes(),
      this.fetchExpandedDisplayTypes(),
      this.fetchProcessingPipelines()
    ])

    if (this.gallery && this.gallery.id && this.hasPerm('gallery.settings.favorites_eligibility')) {
      await this.fetchFavouriteEligibility(this.gallery.id)
    }

    this.onReset()
    this.loading = false
  },
  methods: {
    ...mapActions('eventGallery', [
      'fetchTags',
      'fetchGallery',
      'fetchTabData'
    ]),
    ...mapActions('core', [
      'fetchFavouriteEligibility',
      'fetchSubGalleryDisplayTypes',
      'fetchCompactDisplayTypes',
      'fetchExpandedDisplayTypes',
      'fetchProcessingPipelines',
    ]),
    ...mapActions('eventWizard', [
      'fetchEventConfiguration',
    ]),
    onReset() {
      this.form = {...this.step_setting}
    },
    async onSave() {
      try {
        await this.emitPromised('save', this.form)
        // Parent will handle refreshing data after save
        this.onReset()
      } catch {
        // Error already notified by the parent; keep the form values so the user can retry
      }
    },
    onDeleteGallery() {
      this.confirm({
        title: this.$pgettext('event_gallery.delete', 'Delete Gallery'),
        message: this.$pgettext('event_gallery.delete', 'Are you sure you want to delete this gallery?<br>This action cannot be undone'),
        cb_confirm: async () => {
          this.$emit('delete')
        }
      })
    }
  }
}
</script>
